import classNames from 'classnames';
import {observer} from 'mobx-react';
import React from 'react';

import {onEnterAndSpace} from '@udemy/design-system-utils';
import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';

import {type StackableCouponProps} from '../redeem-coupon/redeem-coupon.react-component';
import styles from './available-coupon.module.less';

interface KeyEvent {
    type: string;
    key: string;
}

interface AvailableCouponProps {
    coupons?: StackableCouponProps[];
    currentCodes?: string[];
    onApplyCoupon:
        | ((
              c: StackableCouponProps[],
          ) => (e: React.KeyboardEvent<HTMLButtonElement>) => (e: KeyEvent) => void)
        | ((c: StackableCouponProps[]) => void);
}

export const AvailableCoupon: React.FC<AvailableCouponProps> = observer(
    ({coupons = [], currentCodes = [], onApplyCoupon}) => {
        const {gettext, interpolate} = useI18n();

        const getCouponDetail = (displayedCoupons: StackableCouponProps[]) => {
            if (displayedCoupons[0].savings_type == 'volume_min_qty') {
                return (
                    <div>
                        {gettext('Buy')}{' '}
                        <span className={styles['coupon-savings-percent']}>
                            {interpolate(
                                gettext('%(savings_value)s or more'),
                                {savings_value: displayedCoupons[0].savings_value},
                                true,
                            )}
                        </span>{' '}
                        {gettext('courses and take')}{' '}
                        <span className={styles['coupon-savings-percent']}>
                            {interpolate(
                                gettext('an extra %(rate)s off'),
                                {rate: displayedCoupons[0].rate},
                                true,
                            )}
                        </span>
                    </div>
                );
            }
            return (
                <div>
                    <span className={styles['coupon-savings-percent']}>
                        {interpolate(
                            gettext('Extra %(rate)s off'),
                            {rate: displayedCoupons[0].rate},
                            true,
                        )}
                    </span>{' '}
                    {gettext('courses')}
                </div>
            );
        };

        const displayedCoupons = coupons.filter((c) => !currentCodes.includes(c.code));

        if (displayedCoupons.length == 0) {
            return null;
        }

        return (
            <div className={styles['available-coupon-container']}>
                <div className={styles['coupon-detail']}>
                    {getCouponDetail(displayedCoupons)}
                    <div
                        className={classNames(
                            'ud-clp-available-coupon-code',
                            styles['available-coupon-code'],
                        )}
                        data-toggle="tooltip"
                        title={coupons[0].code}
                    >
                        {displayedCoupons[0].code}
                    </div>
                </div>
                <div className={styles['apply-code-wrapper']}>
                    <Button
                        udStyle="ghost"
                        size="medium"
                        className={classNames(
                            'ud-clp-apply-available-coupon-code',
                            styles.pointer,
                            styles['apply-button'],
                            'ud-link-underline',
                        )}
                        typography="ud-text-md"
                        onKeyDown={(e) => {
                            const func = onApplyCoupon(coupons);
                            if (typeof func === 'function') {
                                onEnterAndSpace(func(e));
                            }
                        }}
                        onClick={() => onApplyCoupon(coupons)}
                        role="button"
                        tabIndex={0}
                    >
                        {gettext('Apply')}
                    </Button>
                </div>
            </div>
        );
    },
);
